import { Tooltip as KitTooltip } from '@ibsys/kit';
import { cloneElement, FC, FocusEvent, PointerEvent, ReactElement, ReactNode, useEffect } from 'react';
import { cn, isElementTruncated, isKeyboardNavigationFocus, trackKeyboardNavigation } from '@/shared/lib';
import styles from './Tooltip.module.css';

/** Класс начинки подсказки, снимающий указатель со всей подсказки; правило — в `Tooltip.module.css` */
const CONTENT_TRANSPARENT_TO_POINTER_CLASS = styles.content;

/** Класс начинки подсказки, сдвигающий её от края элемента к месту наведения */
const CONTENT_AT_POINTER_CLASS = styles.atPointer;

/** Куда подсказке сдвинуться от края элемента, чтобы встать у указателя; читает их CSS начинки */
const POINTER_OFFSET_PROPERTIES = {
  centerX: '--tooltip-pointer-center-x',
  centerY: '--tooltip-pointer-center-y',
  top: '--tooltip-pointer-top',
  bottom: '--tooltip-pointer-bottom',
  left: '--tooltip-pointer-left',
  right: '--tooltip-pointer-right',
};

/**
 * Зазор между указателем и подсказкой, px: у края элемента подсказку отделяет от указателя ещё и
 * половина самого элемента, и вплотную к указателю она не встаёт — у места наведения зазор задаётся
 * тут, по половине кнопки-значка.
 */
const POINTER_CLEARANCE = 18;

/**
 * Запоминает место наведения — смещением от каждого края элемента до указателя.
 *
 * Подсказка живёт в портале и встаёт у края элемента; нашему коду из неё доступна только начинка,
 * поэтому смещение к указателю передаётся ей стилями через корень документа, а сдвигает себя
 * начинка сама. С какой стороны подсказка зашла, решает библиотека — смещения пишутся от всех
 * краёв сразу, а нужную пару выбирает CSS по стороне показа.
 */
const rememberPointerPlace = (element: HTMLElement, pointerX: number, pointerY: number) => {
  const place = element.getBoundingClientRect();
  const { style } = element.ownerDocument.documentElement;

  style.setProperty(POINTER_OFFSET_PROPERTIES.centerX, `${pointerX - (place.left + place.right) / 2}px`);
  style.setProperty(POINTER_OFFSET_PROPERTIES.centerY, `${pointerY - (place.top + place.bottom) / 2}px`);
  style.setProperty(POINTER_OFFSET_PROPERTIES.top, `${pointerY - POINTER_CLEARANCE - place.top}px`);
  style.setProperty(POINTER_OFFSET_PROPERTIES.bottom, `${pointerY + POINTER_CLEARANCE - place.bottom}px`);
  style.setProperty(POINTER_OFFSET_PROPERTIES.left, `${pointerX - POINTER_CLEARANCE - place.left}px`);
  style.setProperty(POINTER_OFFSET_PROPERTIES.right, `${pointerX + POINTER_CLEARANCE - place.right}px`);
};

/** Возвращает подсказку к краю элемента: у перехода с клавиатуры места наведения нет */
const forgetPointerPlace = (element: HTMLElement) => {
  const { style } = element.ownerDocument.documentElement;

  Object.values(POINTER_OFFSET_PROPERTIES).forEach((property) => style.removeProperty(property));
};

/** Обработчики, которые подсказка добавляет своему элементу поверх его собственных */
type TriggerProps = {
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onPointerMove?: (event: PointerEvent<HTMLElement>) => void;
};

type Props = {
  /** Содержимое подсказки */
  tooltip: ReactNode;
  /** Классы содержимого подсказки */
  classNames?: { content?: string };
  /** Закрывать подсказку при прокрутке любого внешнего контейнера; по умолчанию — да */
  closeOnScroll?: boolean;
  /** Показывать подсказку, только когда содержимое элемента не помещается и усечено */
  onlyWhenTruncated?: boolean;
  /** Показывать подсказку у места наведения, а не у края элемента */
  atPointer?: boolean;
  /** Элемент, к которому относится подсказка */
  children: ReactElement<TriggerProps>;
};

/**
 * Подсказка при наведении — единственная точка, где задано, когда система её показывает.
 *
 * Правило одно на весь интерфейс: подсказка отвечает на наведение курсора и на переход к элементу
 * с клавиатуры, но не на фокус, который окно ставит само — ни открываясь (иначе подсказка первого
 * элемента всплывает поверх того, ради чего окно и открывали), ни закрываясь (иначе подсказка
 * всплывает у кнопки, к которой окно вернуло фокус). Курсор при этом стоит в стороне.
 *
 * Переход пользователя отличает нажатие клавиши перехода перед фокусом ({@link
 * isKeyboardNavigationFocus}) вместе с `:focus-visible`: одного `:focus-visible` мало — фокус,
 * возвращённый окном, закрытым с клавиатуры, браузер тоже считает клавиатурным.
 *
 * С `onlyWhenTruncated` подсказка дублирует видимый текст лишь тогда, когда он не поместился:
 * усечение проверяется в момент показа, поэтому правило само отрабатывает и на смене ширины.
 *
 * Оба запрета выражены отменой события: отменённое событие подсказка пропускает, и собственные
 * обработчики элемента при этом остаются на месте.
 *
 * Показанная подсказка указатель не перехватывает ({@link CONTENT_TRANSPARENT_TO_POINTER_CLASS}):
 * наведение и нажатие проходят сквозь неё к элементу, который она собой закрыла.
 *
 * По умолчанию подсказка встаёт у края элемента; с `atPointer` — у места, на которое навели
 * ({@link rememberPointerPlace}), и держится его, пока указатель идёт по элементу. Второе нужно
 * элементу, вытянутому настолько, что его край далеко от указателя: у края такая подсказка
 * остаётся незамеченной. Переход с клавиатуры места наведения не даёт — подсказка возвращается
 * к краю ({@link forgetPointerPlace}). Пока нажата кнопка, место не запоминается: элемент тогда
 * тянут (ручку растягивания), подсказка в жесте не показывается, а запись в корень документа на
 * каждое движение пересчитывала бы стили всей страницы и тормозила бы сам жест.
 *
 * Кнопку, открывающую окно, подсказка оборачивает вместе с её триггером (`<Tooltip><DialogTrigger
 * asChild><Button/></DialogTrigger></Tooltip>`): триггер передаёт кнопке свои свойства сам, и
 * вставленная между ними подсказка их потеряет — окно перестанет открываться.
 */
export const Tooltip: FC<Props> = ({ tooltip, classNames, closeOnScroll, onlyWhenTruncated, atPointer, children }) => {
  useEffect(trackKeyboardNavigation, []);

  const handleFocus = (event: FocusEvent<HTMLElement>) => {
    children.props.onFocus?.(event);
    if (event.defaultPrevented) return;

    const isUserFocus = isKeyboardNavigationFocus() && event.currentTarget.matches(':focus-visible');
    if (!isUserFocus || (onlyWhenTruncated && !isElementTruncated(event.currentTarget))) {
      event.preventDefault();
      return;
    }

    if (atPointer) forgetPointerPlace(event.currentTarget);
  };

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    children.props.onPointerMove?.(event);
    if (event.defaultPrevented) return;

    if (onlyWhenTruncated && !isElementTruncated(event.currentTarget)) {
      event.preventDefault();
      return;
    }

    if (atPointer && event.buttons === 0) rememberPointerPlace(event.currentTarget, event.clientX, event.clientY);
  };

  return (
    <KitTooltip
      tooltip={tooltip}
      classNames={{
        content: cn(CONTENT_TRANSPARENT_TO_POINTER_CLASS, atPointer && CONTENT_AT_POINTER_CLASS, classNames?.content),
      }}
      closeOnScroll={closeOnScroll}
    >
      {cloneElement(children, { onFocus: handleFocus, onPointerMove: handlePointerMove })}
    </KitTooltip>
  );
};
